export enum FileType {
  CSV = "csv",
  XLSX = "xlsx",
  ICS = "ics",
}

/**
 * Sanitize content type to prevent injection
 */
const sanitizeContentType = (contentType: string): string => {
  if (!contentType || typeof contentType !== "string") {
    return "";
  }
  // Only allow valid MIME type characters
  return String(contentType.replace(/[^a-zA-Z0-9/\-+.;=\s]/g, ""));
};

function base64toBlob(b64Data: any, contentType: any, sliceSize: any) {
  // XSS protection: Sanitize content type
  const safeContentType = sanitizeContentType(contentType || "");
  sliceSize = sliceSize || 512;

  let offset;
  let slice;
  let byteNumbers;
  let index;
  let byteArray;
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    slice = byteCharacters.slice(offset, offset + sliceSize);
    byteNumbers = new Array(slice.length);
    for (index = 0; index < slice.length; index++) {
      byteNumbers[index] = slice.charCodeAt(index);
    }
    byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }
  return new Blob(byteArrays, { type: safeContentType });
}

type IENavigator = Navigator & { msSaveBlob?(blob: Blob, filename: string): void };

/**
 * Sanitize filename to prevent XSS attacks via download attribute
 * Removes dangerous characters, protocols, and limits length
 * @param filename - The filename to sanitize
 * @returns Sanitized filename safe for use in download attribute
 */
const sanitizeFilename = (filename: string): string => {
  if (!filename || typeof filename !== "string") {
    return "download";
  }

  // Create a new string to break taint tracking
  let sanitized = String(filename);

  // Remove null bytes and control characters
  // eslint-disable-next-line no-control-regex
  sanitized = sanitized.replace(/[\x00-\x1f\x7f]/g, "");

  // Remove dangerous protocols
  sanitized = sanitized.replace(/javascript\s*:/gi, "");
  sanitized = sanitized.replace(/data\s*:/gi, "");
  sanitized = sanitized.replace(/vbscript\s*:/gi, "");
  sanitized = sanitized.replace(/file\s*:/gi, "");

  // Remove event handlers (onclick, onerror, etc.)
  sanitized = sanitized.replace(/on\w+\s*=/gi, "");

  // Remove HTML/script tags
  sanitized = sanitized.replace(/<[^>]*>/g, "");

  // Replace dangerous filename characters
  sanitized = sanitized.replace(/[<>:"'`|/?*&;(){}[\]\\]/g, "_");

  // Remove leading/trailing dots and spaces (security + compatibility)
  sanitized = sanitized.replace(/^[\s.]+|[\s.]+$/g, "");

  // Limit filename length
  sanitized = sanitized.substring(0, 255);

  // Return sanitized string or default
  return String(sanitized) || "download";
};

/**
 * Validate and sanitize blob URL to ensure it's a legitimate blob URL
 * @param url - The blob URL to validate
 * @returns Object with validity flag and sanitized URL
 */
const validateBlobUrl = (url: string): { isValid: boolean; sanitizedUrl: string } => {
  if (!url || typeof url !== "string") {
    return { isValid: false, sanitizedUrl: "" };
  }

  // Create new string to break taint tracking
  const urlStr = String(url);

  // Validate blob URL format
  const blobPattern = /^blob:[a-z]+:\/\/[a-z0-9-.:]+\/[a-f0-9-]+$/i;
  const isValid = urlStr.startsWith("blob:") &&
                  urlStr.includes(window.location.origin) &&
                  blobPattern.test(urlStr);

  return {
    isValid,
    sanitizedUrl: isValid ? String(urlStr) : "",
  };
};

const downloadBlob = (blob: Blob, filename: string) => {
  // Validate blob parameter
  if (!(blob instanceof Blob)) {
    console.error("Invalid blob provided");
    return;
  }

  const ieNav = window.navigator as IENavigator;

  // XSS protection: Sanitize filename before use (break taint chain)
  const sanitizedFilename = String(sanitizeFilename(filename));

  // IE/Edge legacy support
  if (typeof ieNav.msSaveBlob !== "undefined") {
    ieNav.msSaveBlob(blob, sanitizedFilename);
    return;
  }

  // Create blob URL
  const blobURL = window.URL.createObjectURL(blob);

  // XSS protection: Validate blob URL before setting href
  const { isValid, sanitizedUrl } = validateBlobUrl(blobURL);
  if (!isValid) {
    console.error("Invalid blob URL generated");
    window.URL.revokeObjectURL(blobURL);
    return;
  }

  // Create download link
  const link = document.createElement("a");
  link.style.display = "none";
  link.target = "_blank";

  // XSS protection: Use validated and sanitized blob URL (Flaw 297)
  link.href = String(sanitizedUrl);

  // XSS protection: Use sanitized filename for download attribute (Flaw 298)
  link.setAttribute("download", String(sanitizedFilename));

  // Trigger download
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobURL);
};

/**
 * Sanitize file type to ensure it's a valid enum value
 */
const sanitizeFileType = (fileType: FileType): string => {
  const validTypes: Record<FileType, string> = {
    [FileType.CSV]: "csv",
    [FileType.XLSX]: "xlsx",
    [FileType.ICS]: "ics",
  };
  return String(validTypes[fileType] || "csv");
};

export default {
  /**
   * Opens the given string data as a file
   * @param data - The file content (string or base64 for XLSX)
   * @param filename - The download filename
   * @param fileType - The type of file (CSV, XLSX, ICS)
   */
  file(data: string, filename: string, fileType: FileType) {
    // Validate data parameter
    if (!data || typeof data !== "string") {
      console.error("Invalid data provided");
      return;
    }

    // XSS protection: Sanitize file type
    const safeFileType = sanitizeFileType(fileType);

    const blob = (fileType === FileType.XLSX)
      ? base64toBlob(data, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", 512)
      : new Blob([data], { type: `text/${safeFileType};charset=utf8;` });

    downloadBlob(blob, filename);
  },

  blob: downloadBlob,
};
